// ─────────────────────────────────────────────────────────
// services/idbSync.ts — High-level IDB ↔ Zustand sync
//
// Two directions:
//   idbLoad()  — read all IDB stores → populate Zustand (instant, offline-safe)
//   idbWrite() — write a full LoadedData snapshot → all IDB stores
//
// Called by:
//   auth.ts loginSuccess  → idbLoad  (seed UI before network query)
//   useCompanyData.ts     → idbWrite (persist fresh Supabase data to IDB)
//   repository.ts fbSave  → idbPut   (write individual record to IDB)
//   repository.ts fbDel   → idbDelete(remove record from IDB)
// ─────────────────────────────────────────────────────────
import {
  idbGetAll, idbReplaceAll, idbGetMeta, idbSetMeta,
} from './idb'
import { useDataStore }      from '@/store/dataStore'
import type { LoadedData }   from './repository'
import type {
  Project, BOQItem, Milestone, DPREntry, Tender,
  EMD, Receivable, RetentionRelease, Expense, CashFlowEntry,
  PettyCashEntry, BankReconEntry, Vendor, PurchaseOrder,
  Quotation, ContractorInvoice, Employee, AttSheet,
  SalaryRecord, Advance, Insurance, TDSEntry, EInvoice,
  Task, AuditEntry, Notification, Brand,
} from '@/types'

// ── Attendance IDB row shape ──────────────────────────────
interface AttRow { id: string; data: AttSheet }
interface SalRow { id: string; data: SalaryRecord }
interface BrandRow { id: 'config'; data: Partial<Brand> }

// ─────────────────────────────────────────────────────────
// idbLoad — read IDB → hydrate Zustand
// Returns true if IDB had data, false if empty.
// ─────────────────────────────────────────────────────────
export async function idbLoad(companyId: string): Promise<boolean> {
  try {
    // Guard: only load if IDB meta matches current company
    const meta = await idbGetMeta()
    if (meta?.companyId !== companyId) return false

    const store = useDataStore.getState()

    // Standard array collections — parallel reads
    const [
      proj, boq, milestones, daily, tender, emd,
      recv, ret, exp, cf, petty, recon, vendor, po,
      quotations, invoice, emp, adv, ins, tds,
      einvoices, task, audit, notif,
    ] = await Promise.all([
      idbGetAll<Project>           ('proj'),
      idbGetAll<BOQItem>           ('boq'),
      idbGetAll<Milestone>         ('milestones'),
      idbGetAll<DPREntry>          ('daily'),
      idbGetAll<Tender>            ('tender'),
      idbGetAll<EMD>               ('emd'),
      idbGetAll<Receivable>        ('recv'),
      idbGetAll<RetentionRelease>  ('ret'),
      idbGetAll<Expense>           ('exp'),
      idbGetAll<CashFlowEntry>     ('cf'),
      idbGetAll<PettyCashEntry>    ('petty'),
      idbGetAll<BankReconEntry>    ('recon'),
      idbGetAll<Vendor>            ('vendor'),
      idbGetAll<PurchaseOrder>     ('po'),
      idbGetAll<Quotation>         ('quotations'),
      idbGetAll<ContractorInvoice> ('invoice'),
      idbGetAll<Employee>          ('emp'),
      idbGetAll<Advance>           ('adv'),
      idbGetAll<Insurance>         ('ins'),
      idbGetAll<TDSEntry>          ('tds'),
      idbGetAll<EInvoice>          ('einvoices'),
      idbGetAll<Task>              ('task'),
      idbGetAll<AuditEntry>        ('audit'),
      idbGetAll<Notification>      ('notif'),
    ])

    // Attendance: stored as { id: monthKey, data: AttSheet }
    const attRows = await idbGetAll<AttRow>('attendance')
    const att: Record<string, AttSheet> = {}
    attRows.forEach(r => { att[r.id] = r.data })

    // Salaries: stored as { id: sk, data: SalaryRecord }
    const salRows = await idbGetAll<SalRow>('salaries')
    const sal: Record<string, SalaryRecord> = {}
    salRows.forEach(r => { sal[r.id] = r.data })

    // Check if there's anything worth hydrating
    const hasData = proj.length > 0 || emp.length > 0 || exp.length > 0
      || vendor.length > 0 || recv.length > 0 || attRows.length > 0

    if (!hasData) return false

    // Hydrate Zustand — all at once
    store.setProj(proj);         store.setBoq(boq)
    store.setMilestones(milestones); store.setDaily(daily)
    store.setTender(tender);     store.setEmd(emd)
    store.setRecv(recv);         store.setRet(ret)
    store.setExp(exp);           store.setCf(cf)
    store.setPetty(petty);       store.setRecon(recon)
    store.setVendor(vendor);     store.setPo(po)
    store.setQuotations(quotations); store.setInvoice(invoice)
    store.setEmp(emp);           store.setAdv(adv)
    store.setIns(ins);           store.setTds(tds)
    store.setEinvoices(einvoices); store.setTask(task)
    store.setAudit(audit);       store.setNotif(notif)
    store.setAttAll(att)
    Object.entries(sal).forEach(([k, v]) => store.setSal(k, v))

    return true
  } catch (e) {
    console.warn('[idbSync] load failed:', e)
    return false
  }
}

// ─────────────────────────────────────────────────────────
// idbWrite — persist a full LoadedData snapshot to IDB
// Called after a successful Supabase fetch.
// ─────────────────────────────────────────────────────────
export async function idbWrite(
  companyId: string,
  data: LoadedData
): Promise<void> {
  try {
    // Write all standard array collections in parallel
    await Promise.all([
      idbReplaceAll('proj',       data.proj),
      idbReplaceAll('boq',        data.boq),
      idbReplaceAll('milestones', data.milestones),
      idbReplaceAll('daily',      data.daily),
      idbReplaceAll('tender',     data.tender),
      idbReplaceAll('emd',        data.emd),
      idbReplaceAll('recv',       data.recv),
      idbReplaceAll('ret',        data.ret),
      idbReplaceAll('exp',        data.exp),
      idbReplaceAll('cf',         data.cf),
      idbReplaceAll('petty',      data.petty),
      idbReplaceAll('recon',      data.recon),
      idbReplaceAll('vendor',     data.vendor),
      idbReplaceAll('po',         data.po),
      idbReplaceAll('quotations', data.quotations),
      idbReplaceAll('invoice',    data.invoice),
      idbReplaceAll('emp',        data.emp),
      idbReplaceAll('adv',        data.adv),
      idbReplaceAll('ins',        data.ins),
      idbReplaceAll('tds',        data.tds),
      idbReplaceAll('einvoices',  data.einvoices),
      idbReplaceAll('task',       data.task),
      idbReplaceAll('audit',      data.audit),
      idbReplaceAll('notif',      data.notif),
    ])

    // Attendance: reshape Record<monthKey, AttSheet> → AttRow[]
    const attRows: AttRow[] = Object.entries(data.att)
      .map(([id, sheet]) => ({ id, data: sheet }))
    await idbReplaceAll('attendance', attRows as unknown as Array<object & { id: string }>)

    // Salaries: reshape Record<sk, SalaryRecord> → SalRow[]
    const salRows: SalRow[] = Object.entries(data.sal)
      .map(([id, rec]) => ({ id, data: rec }))
    await idbReplaceAll('salaries', salRows as unknown as Array<object & { id: string }>)

    // Brand
    if (data.brand?.name) {
      const brandRow: BrandRow = { id: 'config', data: data.brand }
      await idbReplaceAll('brand', [brandRow as unknown as object & { id: string }])
    }

    // Update meta
    await idbSetMeta({ companyId, lastSyncAt: new Date().toISOString() })
  } catch (e) {
    console.warn('[idbSync] write failed:', e)
  }
}

// ─────────────────────────────────────────────────────────
// idbLoadBrand — read brand from IDB (for logo display offline)
// ─────────────────────────────────────────────────────────
export async function idbLoadBrand(): Promise<Partial<Brand> | null> {
  try {
    const rows = await idbGetAll<BrandRow>('brand')
    return rows.find(r => r.id === 'config')?.data ?? null
  } catch {
    return null
  }
}
